import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';

export interface RegistryVessel {
  _id: string;
  name: string;
  imoNumber?: string;
}

export function findRegistryVessel(vessels: RegistryVessel[], vesselName: string) {
  return vessels.find((v) => v._id === vesselName);
}

export function getVesselDisplayName(vessels: RegistryVessel[], vesselName: string) {
  if (!vesselName) return '';
  const name = findRegistryVessel(vessels, vesselName)?.name || vesselName;
  return name.toUpperCase();
}

export function isRegistryVesselSelection(vessels: RegistryVessel[], vesselName: string) {
  return Boolean(findRegistryVessel(vessels, vesselName));
}

interface VesselNameComboboxProps {
  vessels: RegistryVessel[];
  vesselName: string;
  onVesselChange: (vesselName: string, imoNo: string) => void;
  inputClassName?: string;
  labelClassName?: string;
  errorClassName?: string;
  disabled?: boolean;
}

export function VesselNameCombobox({
  vessels,
  vesselName,
  onVesselChange,
  inputClassName,
  labelClassName,
  errorClassName,
  disabled = false,
}: VesselNameComboboxProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [inputText, setInputText] = useState(() => getVesselDisplayName(vessels, vesselName));

  useEffect(() => {
    setInputText(getVesselDisplayName(vessels, vesselName));
  }, [vesselName, vessels]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const query = inputText.trim();
  const normalizedQuery = query.toLowerCase();

  const matchingVessels = useMemo(() => {
    if (!query) return vessels.slice(0, 50);
    return vessels
      .filter((v) => v.name.toLowerCase().includes(normalizedQuery))
      .slice(0, 50);
  }, [vessels, query, normalizedQuery]);

  const hasExactRegistryMatch = useMemo(
    () => vessels.some((v) => v.name.trim().toLowerCase() === normalizedQuery),
    [vessels, normalizedQuery]
  );

  const showAddNew = Boolean(query) && !hasExactRegistryMatch;

  const selectRegistryVessel = (v: RegistryVessel) => {
    const name = (v.name || '').toUpperCase();
    setInputText(name);
    onVesselChange(v._id, v.imoNumber || '');
    setOpen(false);
  };

  const selectCustomVessel = () => {
    const name = query.trim().toUpperCase();
    setInputText(name);
    onVesselChange(name, '');
    setOpen(false);
  };

  const handleInputChange = (value: string) => {
    const upper = value.toUpperCase();
    setInputText(upper);
    setOpen(true);
    if (!upper.trim()) {
      onVesselChange('', '');
    }
  };

  const commitInput = () => {
    const q = inputText.trim().toUpperCase();
    if (!q) {
      onVesselChange('', '');
      return;
    }

    const byName = vessels.find((v) => v.name.trim().toLowerCase() === q.toLowerCase());
    if (byName) {
      selectRegistryVessel(byName);
      return;
    }

    if (isRegistryVesselSelection(vessels, vesselName)) {
      const current = findRegistryVessel(vessels, vesselName);
      if (current?.name.trim().toLowerCase() === q.toLowerCase()) {
        setInputText((current?.name || q).toUpperCase());
        return;
      }
    }

    selectCustomVessel();
  };

  const handleBlur = () => {
    window.setTimeout(() => {
      setOpen(false);
      commitInput();
    }, 150);
  };

  return (
    <div ref={rootRef} className={cn('relative space-y-1.5', open && 'z-40 mb-56')}>
      <label className={labelClassName}>
        Vessel Name <span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        value={inputText}
        disabled={disabled}
        placeholder="TYPE OR SELECT VESSEL"
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        className={cn(inputClassName, errorClassName, 'uppercase')}
        autoComplete="off"
        style={{ textTransform: 'uppercase' }}
      />
      {open && !disabled && (
        <ul className="absolute left-0 right-0 z-[70] mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-lg dark:border-white/15 dark:bg-[#0f172a]">
          {matchingVessels.length === 0 && !showAddNew && (
            <li className="px-3 py-2 text-sm text-[hsl(var(--muted-foreground))]">No vessels found</li>
          )}
          {matchingVessels.map((vessel) => (
            <li key={vessel._id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectRegistryVessel(vessel)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-[#0088cc]/10 dark:hover:bg-[#0088cc]/20"
              >
                <div className="font-semibold uppercase">{vessel.name}</div>
                {vessel.imoNumber && (
                  <div className="text-[10px] text-[hsl(var(--muted-foreground))]">IMO {vessel.imoNumber}</div>
                )}
              </button>
            </li>
          ))}
          {showAddNew && (
            <li>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={selectCustomVessel}
                className="w-full px-3 py-2 text-left text-sm font-bold text-[#0088cc] hover:bg-[#0088cc]/10 dark:hover:bg-[#0088cc]/20"
              >
                + Add new vessel name: {query.toUpperCase()}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
